import { Box } from '@chakra-ui/react';
import { useEffect, useMemo, useRef, type CSSProperties } from 'react';

import {
  mergeStaticPageOverrides,
  staticPageThemeOverrides,
} from '#features/static-pages/constants/staticPageTheme';

type EmbeddedHtmlPageProps = {
  enableSectionAnchors?: boolean;
  extraStyles?: string;
  html: string;
  minHeight?: number;
  runInlineScripts?: boolean;
  shellWidth?: string;
  showBackToTop?: boolean;
  showReadingProgress?: boolean;
  title: string;
};

const BODY_REGEX = /<body[^>]*>([\s\S]*?)<\/body>/i;
const STYLE_REGEX = /<style[^>]*>([\s\S]*?)<\/style>/gi;
const SCRIPT_TAG_REGEX = /<script\b([^>]*)>([\s\S]*?)<\/script>/gi;
const SCRIPT_SRC_ATTR_REGEX = /\bsrc\s*=/i;
const SCRIPT_TYPE_ATTR_REGEX = /\btype\s*=\s*["']?([^"'\s>]+)["']?/i;
const CF_EMAIL_SPAN_REGEX = /<span[^>]*__cf_email__[^>]*>(.*?)<\/span>/gi;
const EMAIL_PLACEHOLDER_REGEX = /\[email(?:&#160;| |&nbsp;|\\u00a0| )protected\]/gi;
const SCOPE_CLASS = '.tp-static-page';

const findMatchingBrace = (value: string, openBraceIndex: number) => {
  let depth = 0;

  for (let i = openBraceIndex; i < value.length; i += 1) {
    const current = value[i];
    const next = value[i + 1];

    if ((current === '"' || current === "'") && value[i - 1] !== '\\') {
      const quote = current;

      i += 1;

      while (i < value.length && (value[i] !== quote || value[i - 1] === '\\')) {
        i += 1;
      }

      continue;
    }

    if (current === '/' && next === '*') {
      i += 2;

      while (i < value.length && !(value[i] === '*' && value[i + 1] === '/')) {
        i += 1;
      }

      i += 1;

      continue;
    }

    if (current === '{') {
      depth += 1;
      continue;
    }

    if (current !== '}') {
      continue;
    }

    depth -= 1;

    if (depth === 0) {
      return i;
    }
  }

  return -1;
};

const scopeCssSelector = (selector: string) => {
  const withoutRootSelectors = selector
    .replaceAll(/:root/g, SCOPE_CLASS)
    .replaceAll(/\bhtml\b/g, SCOPE_CLASS)
    .replaceAll(/\bbody\b/g, SCOPE_CLASS);

  if (withoutRootSelectors.includes(SCOPE_CLASS)) {
    return withoutRootSelectors;
  }

  return `${SCOPE_CLASS} ${withoutRootSelectors}`;
};

const scopeCssSelectorGroup = (selectors: string) => {
  return selectors
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .map(scopeCssSelector)
    .join(', ');
};

const scopeCss = (value: string) => {
  let output = '';
  let cursor = 0;

  while (cursor < value.length) {
    const openBraceIndex = value.indexOf('{', cursor);

    if (openBraceIndex === -1) {
      output += value.slice(cursor);
      break;
    }

    const selector = value.slice(cursor, openBraceIndex).trim();
    const closeBraceIndex = findMatchingBrace(value, openBraceIndex);

    if (closeBraceIndex === -1) {
      output += value.slice(cursor);
      break;
    }

    const block = value.slice(openBraceIndex + 1, closeBraceIndex);

    if (!selector) {
      output += value.slice(cursor, closeBraceIndex + 1);
      cursor = closeBraceIndex + 1;
      continue;
    }

    if (selector.startsWith('@')) {
      const nestedBlock = selector.includes('keyframes') ? block : scopeCss(block);

      output += `${selector} {${nestedBlock}}`;
      cursor = closeBraceIndex + 1;
      continue;
    }

    output += `${scopeCssSelectorGroup(selector)} {${block}}`;
    cursor = closeBraceIndex + 1;
  }

  return output;
};

const isExecutableInlineScript = (attributes: string) => {
  if (SCRIPT_SRC_ATTR_REGEX.test(attributes)) {
    return false;
  }

  const scriptType = attributes.match(SCRIPT_TYPE_ATTR_REGEX)?.[1]?.trim().toLowerCase();

  if (!scriptType) {
    return true;
  }

  return [
    'text/javascript',
    'application/javascript',
    'text/ecmascript',
    'application/ecmascript',
    'module',
  ].includes(scriptType);
};

const extractPageParts = (rawHtml: string) => {
  const normalized = rawHtml
    .replaceAll(/\/cdn-cgi\/l\/email-protection[^"' >]*/gi, 'mailto:support@teamplay.gg')
    .replace(CF_EMAIL_SPAN_REGEX, '$1')
    .replaceAll(/\sdata-cfemail="[^"]*"/gi, '')
    .replaceAll(/class="__cf_email__"/gi, '')
    .replace(EMAIL_PLACEHOLDER_REGEX, 'support@teamplay.gg');

  const inlineScripts: string[] = [];

  const normalizedWithoutScripts = normalized.replace(
    SCRIPT_TAG_REGEX,
    (_match, attributes: string = '', scriptContent: string = '') => {
      if (!isExecutableInlineScript(attributes)) {
        return '';
      }

      const script = scriptContent.trim();

      if (script) {
        inlineScripts.push(script);
      }

      return '';
    }
  );

  const styles = [...normalizedWithoutScripts.matchAll(STYLE_REGEX)]
    .map((match) => match[1]?.trim())
    .filter(Boolean)
    .join('\n');

  const htmlContent = normalizedWithoutScripts.match(BODY_REGEX)?.[1] ?? normalizedWithoutScripts;
  const content = htmlContent.replace(STYLE_REGEX, '').trim();

  return {
    content,
    inlineScripts,
    scopedStyles: scopeCss(styles),
  };
};

const toSectionId = (value: string, index: number) => {
  const normalized = value
    .toLowerCase()
    .trim()
    .replaceAll(/\s+/g, '-')
    .replaceAll(/[^a-z0-9\u0400-\u04ff-]/g, '')
    .replaceAll(/-+/g, '-');

  return normalized || `section-${index + 1}`;
};

export const EmbeddedHtmlPage = ({
  enableSectionAnchors = false,
  extraStyles,
  html,
  minHeight = 800,
  runInlineScripts = false,
  shellWidth = '980px',
  showBackToTop = false,
  showReadingProgress = false,
  title,
}: EmbeddedHtmlPageProps) => {
  const page = useMemo(() => extractPageParts(html), [html]);
  const scopedExtraStyles = useMemo(
    () => scopeCss(mergeStaticPageOverrides(staticPageThemeOverrides, extraStyles)),
    [extraStyles]
  );
  const pageRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const progressBarRef = useRef<HTMLDivElement | null>(null);
  const backToTopRef = useRef<HTMLButtonElement | null>(null);
  const scriptHostRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!runInlineScripts || page.inlineScripts.length === 0 || !scriptHostRef.current) {
      return;
    }

    const scriptElements = page.inlineScripts.map((scriptContent) => {
      const scriptElement = document.createElement('script');

      scriptElement.type = 'text/javascript';
      scriptElement.textContent = scriptContent;
      scriptHostRef.current?.appendChild(scriptElement);

      return scriptElement;
    });

    return () => {
      scriptElements.forEach((scriptElement) => scriptElement.remove());
    };
  }, [page.inlineScripts, runInlineScripts]);

  useEffect(() => {
    if (!enableSectionAnchors || !contentRef.current) {
      return;
    }

    const content = contentRef.current;
    const usedIds = new Set<string>(
      [...content.querySelectorAll<HTMLElement>('[id]')]
        .map((node) => node.id)
        .filter(Boolean)
    );
    const cleanupActions: Array<() => void> = [];

    content.querySelectorAll<HTMLElement>('h2, h3').forEach((heading, index) => {
      if (heading.querySelector('.tp-static-anchor')) {
        return;
      }

      const text = heading.textContent?.trim();

      if (!text) {
        return;
      }

      if (!heading.id) {
        let candidateId = toSectionId(text, index);
        let suffix = 1;

        while (usedIds.has(candidateId)) {
          suffix += 1;
          candidateId = `${toSectionId(text, index)}-${suffix}`;
        }

        heading.id = candidateId;
        usedIds.add(candidateId);
      }

      const anchor = document.createElement('a');
      anchor.className = 'tp-static-anchor';
      anchor.href = `#${heading.id}`;
      anchor.setAttribute('aria-label', 'Copy link to section');
      anchor.textContent = '#';

      const clickHandler = async (event: MouseEvent) => {
        event.preventDefault();

        const hash = `#${heading.id}`;
        const absoluteUrl = `${window.location.origin}${window.location.pathname}${hash}`;
        window.history.replaceState(null, '', hash);

        try {
          await navigator.clipboard.writeText(absoluteUrl);
          anchor.classList.add('is-copied');
          window.setTimeout(() => anchor.classList.remove('is-copied'), 1200);
        } catch {
          window.location.hash = heading.id;
        }
      };

      anchor.addEventListener('click', clickHandler);
      heading.append(anchor);

      cleanupActions.push(() => {
        anchor.removeEventListener('click', clickHandler);
        anchor.remove();
      });
    });

    return () => {
      cleanupActions.forEach((cleanupAction) => cleanupAction());
    };
  }, [enableSectionAnchors, page.content]);

  useEffect(() => {
    if ((!showReadingProgress && !showBackToTop) || !pageRef.current) {
      return;
    }

    const root = pageRef.current;
    let frame = 0;

    const applyReadingState = () => {
      const progressBar = progressBarRef.current;
      const backToTop = backToTopRef.current;
      const rootTop = window.scrollY + root.getBoundingClientRect().top;
      const totalScrollable = Math.max(root.scrollHeight - window.innerHeight, 1);
      const traversed = Math.min(Math.max(window.scrollY - rootTop, 0), totalScrollable);
      const progress = Math.min(Math.max((traversed / totalScrollable) * 100, 0), 100);

      if (progressBar) {
        progressBar.style.setProperty('width', `${progress.toFixed(2)}%`);
      }

      if (backToTop) {
        backToTop.classList.toggle('is-visible', traversed > 420);
      }
    };

    const requestApply = () => {
      if (frame) {
        return;
      }

      frame = window.requestAnimationFrame(() => {
        frame = 0;
        applyReadingState();
      });
    };

    applyReadingState();
    window.addEventListener('scroll', requestApply, { passive: true });
    window.addEventListener('resize', requestApply);

    return () => {
      if (frame) {
        window.cancelAnimationFrame(frame);
      }

      window.removeEventListener('scroll', requestApply);
      window.removeEventListener('resize', requestApply);
    };
  }, [page.content, showBackToTop, showReadingProgress]);

  return (
    <Box w="100%" minH={`${minHeight}px`}>
      {page.scopedStyles ? <style dangerouslySetInnerHTML={{ __html: page.scopedStyles }} /> : null}
      {scopedExtraStyles ? <style dangerouslySetInnerHTML={{ __html: scopedExtraStyles }} /> : null}
      <Box
        ref={pageRef}
        className="tp-static-page"
        aria-label={title}
        style={{ '--tp-shell-width': shellWidth } as CSSProperties}
      >
        <Box className={`tp-static-progress${showReadingProgress ? '' : ' is-hidden'}`} aria-hidden>
          <Box ref={progressBarRef} className="tp-static-progress-bar" />
        </Box>

        <Box className="tp-static-shell">
          <Box
            ref={contentRef}
            className="tp-static-content"
            dangerouslySetInnerHTML={{ __html: page.content }}
          />
        </Box>

        <button
          ref={backToTopRef}
          type="button"
          className={`tp-static-backtop${showBackToTop ? '' : ' is-hidden'}`}
          aria-label="Back to top"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          Back to top
        </button>
      </Box>
      <Box ref={scriptHostRef} display="none" aria-hidden />
    </Box>
  );
};
